import React, { useState, useEffect, useCallback } from 'react';
import eventBus from '../core/EventBus';

function AssistantOverlay() {
  const [spotlight, setSpotlight] = useState(null); // { rect, label }
  const [highlights, setHighlights] = useState([]); // [{ id, rect }]

  // ── Spotlight handler ──
  const handleSpotlight = useCallback((data) => {
    const el = document.querySelector(data.selector);
    if (!el) return;

    const rect = el.getBoundingClientRect();
    setSpotlight({
      rect: {
        top: rect.top - 6,
        left: rect.left - 6,
        width: rect.width + 12,
        height: rect.height + 12,
      },
      label: data.label || '',
    });

    // Auto-dismiss after 4 seconds
    setTimeout(() => setSpotlight(null), 4000);
  }, []);

  // ── Highlight handler ──
  const handleHighlight = useCallback((data) => {
    const el = document.querySelector(data.selector);
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const id = Date.now() + Math.random();
    const entry = {
      id,
      rect: {
        top: rect.top - 4,
        left: rect.left - 4,
        width: rect.width + 8,
        height: rect.height + 8,
      },
    };

    setHighlights((prev) => [...prev, entry]);

    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      setHighlights((prev) => prev.filter((h) => h.id !== id));
    }, 3000);
  }, []);

  // ── Subscribe to events ──
  useEffect(() => {
    const unsubs = [
      eventBus.on('assistant:spotlight', handleSpotlight),
      eventBus.on('assistant:highlight', handleHighlight),
    ];
    return () => unsubs.forEach((u) => u());
  }, [handleSpotlight, handleHighlight]);

  // Dismiss spotlight on click
  const dismissSpotlight = useCallback(() => {
    setSpotlight(null);
  }, []);

  // Compute arrow position (pointing down towards the element)
  const getArrowStyle = (rect) => {
    return {
      left: rect.left + rect.width / 2 - 12,
      top: rect.top - 36,
    };
  };

  // Compute label position
  const getLabelStyle = (rect) => {
    const viewportW = window.innerWidth;
    const labelLeft = rect.left + rect.width / 2;
    // If element is in the right half, show label to the left
    if (labelLeft > viewportW * 0.6) {
      return {
        right: viewportW - rect.left + 12,
        top: rect.top + rect.height / 2,
        transform: 'translateY(-50%)',
        textAlign: 'right',
      };
    }
    // Otherwise show label to the right
    return {
      left: rect.left + rect.width + 12,
      top: rect.top + rect.height / 2,
      transform: 'translateY(-50%)',
    };
  };

  return (
    <>
      {/* ── Spotlight overlay ── */}
      {spotlight && (
        <div
          className="fixed inset-0 z-[600] cursor-pointer"
          onClick={dismissSpotlight}
        >
          {/* Dark overlay with cutout */}
          <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
            <defs>
              <mask id="spotlight-mask">
                <rect width="100%" height="100%" fill="white" />
                <rect
                  x={spotlight.rect.left}
                  y={spotlight.rect.top}
                  width={spotlight.rect.width}
                  height={spotlight.rect.height}
                  rx="8"
                  fill="black"
                />
              </mask>
            </defs>
            <rect
              width="100%"
              height="100%"
              fill="rgba(0,0,0,0.5)"
              mask="url(#spotlight-mask)"
            />
          </svg>

          {/* Pulsing border around the element */}
          <div
            className="absolute rounded-lg spotlight-ring"
            style={{
              top: spotlight.rect.top,
              left: spotlight.rect.left,
              width: spotlight.rect.width,
              height: spotlight.rect.height,
              pointerEvents: 'none',
            }}
          />

          {/* Animated arrow pointing to element */}
          <div
            className="absolute assistant-arrow"
            style={getArrowStyle(spotlight.rect)}
          >
            <svg width="24" height="30" viewBox="0 0 24 30" fill="none">
              <path d="M12 0 L12 20" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" />
              <path d="M5 15 L12 24 L19 15" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          {/* Label */}
          {spotlight.label && (
            <div
              className="absolute px-3 py-1.5 bg-blue-500 text-white text-xs font-semibold rounded-lg shadow-lg whitespace-nowrap animate-fadeIn"
              style={getLabelStyle(spotlight.rect)}
            >
              {spotlight.label}
              <div className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-blue-500 rotate-45"
                style={
                  getLabelStyle(spotlight.rect).right
                    ? { right: -4 }
                    : { left: -4 }
                }
              />
            </div>
          )}

          {/* Hint text at bottom */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 text-xs animate-fadeIn">
            Klicke irgendwo zum Schließen
          </div>
        </div>
      )}

      {/* ── Highlight rings (no dark overlay) ── */}
      {highlights.map((h) => (
        <div
          key={h.id}
          className="fixed z-[600] rounded-lg pointer-events-none highlight-ring"
          style={{
            top: h.rect.top,
            left: h.rect.left,
            width: h.rect.width,
            height: h.rect.height,
          }}
        />
      ))}
    </>
  );
}

export default AssistantOverlay;
